<template>
  <MainScrollItem
    :item="result"
    tag="article"
    class="x-result x-group/result x-flex x-flex-col x-gap-4 x-rounded-md hover:x-shadow-result"
    data-wysiwyg="result"
    :data-wysiwyg-id="result.id"
    :data-wysiwyg-image-url="result.images ? result.images[0] : ''"
    :data-wysiwyg-title="result.name"
  >
    <div class="x-relative">
      <BaseResultLink class="x-result__picture" :result="result">
        <BaseResultImage
          class="x-rounded-t-md"
          :result="result"
          :load-animation="imageAnimation"
          show-next-image-on-hover
        >
          <template #placeholder>
            <BasePlaceholderImage />
          </template>
          <template #fallback>
            <BaseFallbackImage />
          </template>
        </BaseResultImage>
      </BaseResultLink>

      <div v-if="!result.availability">
        <span
          class="x-text1 x-absolute x-left-0 x-top-0 x-h-[15px] x-rounded-full x-bg-lead-50 x-px-8 x-text-[10px] x-font-light x-uppercase x-text-neutral-0"
        >
          {{ $t('result.outOfStock') }}
        </span>
      </div>
    </div>

    <BaseResultLink
      class="x-result__description x-flex x-flex-col x-gap-2 x-p-16 desktop:x-gap-4"
      :result="result"
    >
      <div class="x-flex x-h-[132px] x-flex-col x-gap-4">
        <span
          class="x-text1 x-text1-sm x-mb-[6px] x-font-alternative x-font-light x-text-neutral-90 desktop:x-text-[16px]"
          >{{ result.brand }}</span>
        <h2
          class="x-text1 x-text1-lg x-line-clamp-2 x-leading-6 x-text-lead-90"
          :class="isRecommendation ? 'desktop:x-text-[16px]' : 'desktop:x-text-[22px]'"
          data-test="result-title"
        >
          {{ result.name }}
        </h2>
        <span
          v-if="showDescription"
          class="x-text1 x-text1-sm x-line-clamp-2 x-font-alternative x-font-light x-leading-6 x-text-neutral-90 desktop:x-text-[16px]"
          >{{ result.description }}</span>
      </div>

      <div class="x-mt-[10px] x-flex x-flex-wrap x-items-center x-gap-8">
        <BaseResultCurrentPrice
          :result="result"
          class="x-title2 x-text-[20px] x-font-bold x-text-lead-90 desktop:x-text-[24px]"
        />
        <BaseResultPreviousPrice
          :result="result"
          class="x-text2 x-text2-lg x-font-medium x-leading-[1.7] x-line-through desktop:x-text-[18px]"
          :class="result.price?.hasDiscount ? 'x-text-lead-50' : 'x-text-lead-90'"
        />
      </div>

      <div>
        <span
          v-if="!result.availability"
          class="x-button x-title4 x-rounded-full x-border-none x-bg-auxiliary-50 x-text-center x-text-neutral-90 hover:x-bg-auxiliary-50"
        >
          {{ $t('result.outOfStock') }}
        </span>

        <BaseAddToCart
          v-else
          :result="result"
          class="x-button-lead x-w-full x-flex-auto x-rounded-full x-border-none x-bg-lead-75 desktop:x-h-[46px] desktop:x-max-w-[311px]"
        >
          {{ $t('result.addToCart') }}
        </BaseAddToCart>
      </div>
    </BaseResultLink>
  </MainScrollItem>
</template>

<script setup lang="ts">
import type { Result } from '@empathyco/x-types'
import {
  BaseAddToCart,
  BaseFallbackImage,
  BasePlaceholderImage,
  BaseResultCurrentPrice,
  BaseResultImage,
  BaseResultLink,
  BaseResultPreviousPrice,
  CrossFade,
} from '@empathyco/x-components'
import { MainScrollItem } from '@empathyco/x-components/scroll'

interface Props {
  result: Result
  showDescription?: boolean
  showAddToCart?: boolean
  isRecommendation?: boolean
}

withDefaults(defineProps<Props>(), {
  showDescription: true,
  showAddToCart: true,
  isRecommendation: false,
})

const imageAnimation = CrossFade
</script>
<style lang="scss">
.x-result-picture-image {
  filter: invert(0%) brightness(95%);
  overflow-anchor: none;
  background-color: white;
  opacity: 0.95;
}
</style>
